import React, { useEffect, useMemo, useState } from 'react';
import { faSquarePlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { ToolButton } from '../../../../components';
import { hideAlert, showAlert } from '../../../../components/alert/Alert';
import { AlertTypeEnum } from '../../../../enums';
import { IToolButtonProps } from '../../../../interfaces';
import { cardsDateService } from '../../services';
import { TextField } from '../../../../components/fields';
import { publish, useStateReducer } from '../../../../hooks';
import { UserPermissions } from '../../../../../src/definitions';
import { securityService } from '../../../../../src/_global/services';
import { whiteSpaceRegex } from '../../../../definitions/Regexes';

const checkValidId = (id: string) => {
  return whiteSpaceRegex.test(id) || id.length <= 0;
};

export default function AddButton({ config }: IToolButtonProps) {
  const navigate = useNavigate();
  const { id: moduleId } = config;
  const [state, setState] = useStateReducer({ id: '', isOpened: false });
  const [options, setOptions] = useState<any>([]);

  const isIdError = checkValidId(state.id);
  const preventSpace = (event: React.KeyboardEvent) => {
    if (event.key === ' ') {
      event?.preventDefault();
    }
  };

  const alertTopic = '__alert_internal';
  const content = useMemo(
    () => (
      <>
        Are you sure you want to add a new card?
        <TextField
          style={{ width: '92%' }}
          label='Please specify the card identifier'
          error={isIdError}
          onKeyDown={preventSpace}
          onChange={({ target }) => setState({ id: target.value })}
          helperText={isIdError && 'Card ID should contain no white spaces'}
        />
      </>
    ),
    [isIdError]
  );

  const handleYes = async () => {
    const {
      data: { _id: newId }
    }: any = await cardsDateService.create(state.id);

    setState({ id: '', isOpened: false });
    hideAlert();
    navigate(`/${moduleId}/${newId}?mode=edit`, { replace: false });
  };

  useEffect(() => {
    if (state.id && !isIdError) {
      setOptions([
        {
          text: 'No',
          onClick: () => {
            setState({ id: '', isOpened: false });
            hideAlert();
          }
        },
        { text: 'Yes', onClick: handleYes, primary: true }
      ]);
    } else {
      setOptions([
        {
          text: 'No',
          onClick: () => {
            setState({ id: '', isOpened: false });
            hideAlert();
          }
        },
        { text: 'Yes', onClick: handleYes, disabled: true, primary: true }
      ]);
    }
  }, [state.id, state.isOpened]);

  useEffect(() => {
    if (state.isOpened)
      publish(alertTopic, {
        content,
        options: {
          actions: options,
          type: AlertTypeEnum.Info
        }
      });
  }, [content, options, state.isOpened]);

  const handleClick = () => {
    setState({ isOpened: true });
    showAlert({
      content: content,
      options: {
        actions: options,
        type: AlertTypeEnum.Info
      }
    });
  };

  return (
    <ToolButton
      text='Add'
      icon={faSquarePlus}
      primary
      raised
      onClick={handleClick}
      invalid={!securityService.checkRoleAccess(UserPermissions.editCards)}
    />
  );
}
